import React, { useState, useEffect, useRef } from 'react';
import { getConversations, getConversation } from '../services/api';
import './Atendimento.css';

const WS_URL = '';

function Atendimento() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Carregar conversas
  useEffect(() => {
    loadConversations();
  }, []);

  // WebSocket
  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => console.log('🔌 WebSocket conectado');
    ws.onerror = (err) => console.error('WebSocket erro:', err);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'NEW_MESSAGE') {
          handleNewMessage(data.conversationId, data.message);
        }
      } catch (e) {
        console.error('Erro ao parsear mensagem WS:', e);
      }
    };

    return () => ws.close();
  }, []);

  // Scroll para baixo quando mensagens mudam
useEffect(() => {
  const container = messagesContainerRef.current;
  if (!container) return;
  
  const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  if (isNearBottom) {
    scrollToBottom();
  }
}, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  async function loadConversations() {
    try {
      setLoading(true);
      const data = await getConversations({}, '-last_message_time', 200);
      const convs = Array.isArray(data) ? data : (data.data || []);
      setConversations(convs);
      if (convs.length > 0) {
        await selectConversation(convs[0]);
      }
    } catch (err) {
      setError('Erro ao carregar conversas: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function selectConversation(conv) {
    try {
      setSelected(conv);
      const fullConv = await getConversation(conv.id);
      setMessages(fullConv.messages || []);
      setSelected(fullConv);
      // Scroll após carregar mensagens
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Erro ao buscar mensagens:', err);
      setMessages(conv.messages || []);
    }
  }

  function handleNewMessage(conversationId, message) {
    setConversations(prev => prev.map(c => 
      c.id === conversationId 
        ? { ...c, last_message: message.content, last_message_time: message.timestamp }
        : c
    ));

    if (selected?.id === conversationId) {
      setMessages(prev => [...prev, message]);
    }
  }

  async function sendMessage() {
    if (!msg.trim() || !selected) return;

    const tempMsg = {
      id: `temp_${Date.now()}`,
      sender: 'agent',
      content: msg,
      timestamp: new Date().toISOString(),
      type: 'text',
      pending: true,
    };

    setMessages(prev => [...prev, tempMsg]);
    setConversations(prev => prev.map(c => 
      c.id === selected.id ? { ...c, last_message: msg, last_message_time: new Date().toISOString() } : c
    ));
    setMsg('');

    try {
      const response = await fetch(`/api/conversations/${selected.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message: msg }),
      });

      if (!response.ok) throw new Error('Falha ao enviar mensagem');

      const result = await response.json();
      
      setMessages(prev => prev.map(m => 
        m.id === tempMsg.id ? result.message : m
      ));
    } catch (err) {
      console.error('Erro ao enviar:', err);
      setMessages(prev => prev.map(m => 
        m.id === tempMsg.id ? { ...m, error: true } : m
      ));
      alert('Erro ao enviar mensagem. Tente novamente.');
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Obter nome de exibição: prioriza nome do cliente associado, depois client_name da conversa
  const getDisplayName = (conv) => {
    if (conv.client && conv.client.full_name) {
      return conv.client.full_name;
    }
    return conv.client_name || 'Cliente';
  };

  const filteredConvs = conversations.filter(c =>
    (getDisplayName(c).toLowerCase().includes(search.toLowerCase())) ||
    (c.client_phone || '').includes(search)
  );

  if (loading) {
    return <div className="loading">Carregando conversas...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="atendimento-page">
      <div className="atendimento-header">
        <h1>Atendimento</h1>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar cliente ou telefone"
            className="search-input"
          />
        </div>
      </div>

      <div className="chat-container">
        {/* Lista de conversas */}
        <div className="conv-list">
          {filteredConvs.map((c) => (
            <div
              key={c.id}
              className={`conv-item ${selected?.id === c.id ? 'active' : ''}`}
              onClick={() => selectConversation(c)}
            >
              <div className="conv-item-top">
                <span className="conv-name">{getDisplayName(c)}</span>
                <span className="conv-time">
                  {c.last_message_time && new Date(c.last_message_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="conv-preview">{c.last_message || 'Nova conversa'}</div>
              <span className={`badge status-${c.status}`}>{c.status}</span>
            </div>
          ))}
        </div>

        {/* Área de chat */}
        <div className="chat-area">
          {selected ? (
            <>
              <div className="chat-header">
                <div>
                  <div className="chat-client-name">{getDisplayName(selected)}</div>
                  <div className="chat-online">{selected.client_phone}</div>
                </div>
                <span className={`badge status-${selected.status}`}>{selected.status}</span>
              </div>

              <div 
                className="messages-area" 
                ref={messagesContainerRef}
              >
                {messages.map((m, idx) => (
                  <div key={m.id || idx} className={`message-wrapper ${m.sender === 'agent' ? 'agent' : 'client'}`}>
                    <div className={`message-bubble ${m.sender} ${m.pending ? 'pending' : ''} ${m.error ? 'error' : ''}`}>
                      {m.content}
                      <span className="message-time">
                        {new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        {m.pending && ' (enviando...)'}
                        {m.error && ' ❌'}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-bar">
                <input
                  type="text"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Digite uma mensagem..."
                  className="chat-input"
                />
                <button className="btn-send" onClick={sendMessage} disabled={!msg.trim()}>
                  ➤ Enviar
                </button>
              </div>
            </>
          ) : (
            <div className="no-conversation">Selecione uma conversa para começar</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Atendimento;